<template>
  <div>

    <!-- Botón para abrir el modal de los campos-->
    <b-button class="mt-1" variant="primary" @click="openModal">
      <span class="text-nowrap text-capitalize">
        {{ $t("match_rules.new_match_rule") }}
      </span>
    </b-button>

    <!-- Modal para los campos-->
    <b-modal v-model="showModal" :title="$t('match_rules.new_match_rule')" @hide="resetForm" hide-footer
      no-close-on-backdrop size="lg">
      <b-form @submit.prevent="saveItem">
        <validation-observer ref="refFormObserver">
          <validation-provider #default="validationContext" :name="$t('match_rules.provider')" rules="required">
            <b-form-group :label="$t('match_rules.second_provider')">
              <v-select :options="providerOptions" v-model="provider" class="w-100"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :reduce="val => val.value"
                :placeholder="$t('match_rules.provider')" />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider #default="validationContext" :name="$t('match_rules.field')" rules="required">
            <b-form-group :label="$t('match_rules.field')">
              <v-select :options="fieldOptions" v-model="selectedField" class="w-100"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider #default="validationContext" :name="$t('match_rules.action')" rules="required">
            <b-form-group :label="$t('match_rules.action')" v-if="selectedField">
              <v-select :options="actionOptions" v-model="form.action" class="w-100"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :reduce="val => val.value" />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider #default="validationContext" :name="$t('match_rules.value')" rules="required">
            <b-form-group :label="$t('match_rules.value')" v-if="form.action">
              <b-form-tags v-if="form.action === 'contains'" v-model="form.value" separator="," required />
              <b-form-input v-else v-model="singleValueText" required />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </validation-observer>



        <div class="d-flex justify-content-end mt-3">
          <b-button variant="primary" class="mr-2" @click="closeModal">
            {{ $t("actions.cancel") }}
          </b-button>
          <b-button type="submit" variant="success">
            {{ $t("actions.save") }}
          </b-button>
        </div>

      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { ref, watch, computed, onMounted, getCurrentInstance, reactive } from "@vue/composition-api";
import vSelect from "vue-select";
import store from "@/store";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import formValidation from "@core/comp-functions/forms/form-validation";



export default {
  components: {
    vSelect,
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    editItem: Object,
    providerItem: Object,
  },
  setup(props, { emit }) {

     // form validation component
    const { proxy } = getCurrentInstance();
    const { refFormObserver, getValidationState } = formValidation();

    const showModal = ref(false);
    const provider = ref('')

    // form for provider fields
    const form = ref({
      action: '',
      field: '',
      valueType: '',
      value: [],
    });

    // selectedField is used to display "contains" value into single value insted of array value
    const selectedField = ref(null);
    const singleValueText = computed({
      get() {
        return Array.isArray(form.value.value) ? form.value.value[0] || '' : form.value.value;
      },
      set(val) {
        form.value.value = [val];
      }
    });

    // Retrieves the list of actions from Vuex
    const actionOptions = computed(() => {
      return store.getters["alarm_action/getSelectedItems"].map(option => ({
        label: proxy.$t(`match_rules_action.${option.description}`),
        value: option.description,
      }));
    });

    // Retrieves the list of fields from Vuex
    const fieldOptions = computed(() => {
      return store.getters["alarm_filter_types/getFields"].map(option => ({
        label: option.description,
        value: option.name,
        fieldType: option.fieldType,
      }));
    });

    // Retrieves the list of providers from props
    const providerOptions = computed(() => {
      if (!props.providerItem || !Array.isArray(props.providerItem.value)) return [];

      return props.providerItem.value.map(provider => ({
        label: provider,
        value: provider,
      }));
    });

    // Fetches alarm actions and filter providers when the component is mounted
    onMounted(() => {
      store.dispatch("alarm_action/getAll");
      store.dispatch("alarm_filter_types/getProviders");
    });

    // Updates form field and type when a new field is selected
    watch(selectedField, (data) => {
      if (data) {
        form.value.field = data.value;
        form.value.valueType = data.fieldType;
      }
    });

    // Watches for changes to the item being edited and opens the match rule modal if its not a provider and camera rule
    watch(() => props.editItem, (newItem) => {
      if (newItem) {
        if (newItem.field !== 'provider' && newItem.field !== 'camera') {
          form.value.action = newItem.action;
          form.value.field = newItem.field;
          form.value.valueType = newItem.valueType;
          form.value.value = [...newItem.value];
          showModal.value = true;
        }
      }
    });

    // Triggers field selection update when provider changes
    watch(provider, (newProvider) => {
      if (newProvider) {
        store.dispatch("alarm_filter_types/getFieldSelection", {
          provider: newProvider,
        });
      }
    });

    const openModal = () => {
      resetForm();
      if (provider.value) {
        store.dispatch("alarm_filter_types/getFieldSelection", { provider: provider.value });
      }
      showModal.value = true;
    };

    const closeModal = () => {
      resetForm();
      showModal.value = false;
    };

    const saveItem = async () => {
      const validation = await refFormObserver.value.validate();
      if (!validation) return;


      if (showModal.value) {
        emit("save", { ...form.value });
        closeModal();
      }

      closeModal();
    };

    const resetForm = () => {
      form.value = { action: '', field: '', valueType: '', value: [] };
      provider.value = '';
      selectedField.value = null;
      singleValueText.value = '';

      //reset fields
      store.commit("alarm_filter_types/resetFields");
    };

    return {
      showModal,
      form,
      openModal,
      closeModal,
      saveItem,
      resetForm,
      actionOptions,
      fieldOptions,
      selectedField,
      singleValueText,
      provider,
      refFormObserver,
      getValidationState,
      providerOptions,
    };
  }

};
</script>